import {
    CalendarDate,
    CalendarDateTime,
    DayOfWeek,
    getCalendarDateTime,
    isNonNilEntityId,
    RecurrencePattern,
    TaskParentId,
    TaskTypeId,
    UserId,
} from "@sp-crm/core";
import {
    firstListedUserId,
    SelectableUser,
    UserSelect,
} from "components/shared/user-select";
import { activityRecordTypeForEntityKind } from "components/tasks/activity-type-options";
import { ActivityTypeSelect } from "components/tasks/activity-type-select";
import { DeleteButton } from "components/ui/action-button";
import { CalendarDateTimeInput } from "components/ui/calendar-date-time";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import { UpdateTaskPayload, useGetUsersForRegionQuery } from "generated/graphql";
import { usePreventUnload } from "hooks/use-prevent-unload";
import React, { useCallback, useMemo } from "react";
import { Link, Prompt } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { RecurrenceConfig } from "./recurrence-config";
import { Taskv2 } from "./types";

interface TaskFormV2Props {
    initial: Taskv2;
    mode: "update" | "create";
    onClose: () => void;
    saveTask: (parentId: TaskParentId, payload: UpdateTaskPayload) => Promise<void>;
    onDelete?: () => Promise<void>;
    entityLinkDisabled?: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
}

export const TaskFormV2: React.FC<TaskFormV2Props> = props => {
    const { task, entity } = props.initial;
    const { onClose, saveTask, mode, onDirtyChange } = props;
    const [changes, setChanges] = React.useState<UpdateTaskPayload>({});
    const [updating, setUpdating] = React.useState(false);
    const regionId = useRegionId();
    const regionUsers = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );
    const users = regionUsers.data?.getUsersForRegion ?? [];
    const storedOwnerId = isNonNilEntityId(task.ownerId) ? task.ownerId : null;
    const defaultOwnerId =
        mode === "create" &&
        regionUsers.isSuccess &&
        !users.some(u => u.id === storedOwnerId)
            ? firstListedUserId(users)
            : storedOwnerId;
    const isDirty = Object.keys(changes).length > 0;
    usePreventUnload(isDirty);
    React.useEffect(() => {
        onDirtyChange?.(isDirty);
        return () => onDirtyChange?.(false);
    }, [isDirty, onDirtyChange]);

    const handleCancel = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setChanges({});
            onClose();
        },
        [onClose, setChanges],
    );

    const handleSave = useCallback(
        async (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const saveParams: UpdateTaskPayload =
                mode === "create"
                    ? {
                          completedAt: task.completedAt,
                          dueDate: task.dueDate,
                          dueDateTime: task.dueDateTime,
                          ownerId: defaultOwnerId,
                          taskTypeId: task.taskTypeId,
                          status: task.status,
                          text: task.text,
                          notes: task.notes,
                          recurrencePattern: task.recurrencePattern as RecurrencePattern,
                          recurrenceInterval: task.recurrenceInterval,
                          recurrenceDaysOfWeek: task.recurrenceDaysOfWeek as DayOfWeek[],
                          recurrenceDayOfMonth: task.recurrenceDayOfMonth,
                          ...changes,
                      }
                    : changes;

            if (Object.keys(saveParams).length > 0) {
                setUpdating(true);
                await saveTask(task.parentId, saveParams);
                setUpdating(false);
            }
            setChanges({});
            onClose();
        },
        [saveTask, setChanges, onClose, task, changes, setUpdating, mode, defaultOwnerId],
    );

    const handleOwnerChange = useCallback(
        (selected: SelectableUser) => {
            const { ownerId: _previousOwnerId, ...otherChanges } = changes;
            setChanges(
                selected && selected !== defaultOwnerId
                    ? { ...otherChanges, ownerId: selected as UserId }
                    : otherChanges,
            );
        },
        [changes, setChanges, defaultOwnerId],
    );

    const handleDueDateChange = useCallback(
        (d: CalendarDateTime | null) => {
            if (!d) {
                setChanges({
                    ...changes,
                    dueDate: null,
                    dueDateTime: null,
                });
                return;
            }
            if (d.value.hasTime) {
                setChanges({
                    ...changes,
                    dueDate: null,
                    dueDateTime: d.value.date,
                });
            } else {
                setChanges({
                    ...changes,
                    dueDate: d.value.date.toString(),
                    dueDateTime: null,
                });
            }
        },
        [changes, setChanges],
    );

    const dueDate: CalendarDate | null = useMemo(() => {
        if ("dueDate" in changes) {
            return CalendarDate.parse(changes.dueDate).getOrElse(null);
        }

        if (task.dueDate) {
            return CalendarDate.parse(task.dueDate).getOrElse(null);
        }

        return null;
    }, [changes.dueDate, task.dueDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const dueDateTime: Date | null = useMemo(() => {
        if ("dueDateTime" in changes) {
            return changes.dueDateTime ? new Date(changes.dueDateTime) : null;
        }

        if (task.dueDateTime) {
            return new Date(task.dueDateTime);
        }

        return null;
    }, [changes.dueDateTime, task.dueDateTime]); // eslint-disable-line react-hooks/exhaustive-deps

    const taskTypeId: TaskTypeId | null = useMemo(() => {
        if ("taskTypeId" in changes) {
            return changes.taskTypeId;
        }

        return task.taskTypeId;
    }, [changes.taskTypeId, task.taskTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTaskTypeChange = useCallback(
        (v: TaskTypeId | null) => {
            setChanges({ ...changes, taskTypeId: v });
        },
        [changes, setChanges],
    );

    const isCompleted = "status" in changes ? changes.status === "done" : task.done;
    const completedAt = "completedAt" in changes ? changes.completedAt : task.completedAt;
    const completedAtValue = completedAt
        ? CalendarDateTime.parse(completedAt).getOrElse(null)
        : null;

    const handleCompletedAtChange = useCallback(
        (d: CalendarDateTime | null) => {
            setChanges({ ...changes, completedAt: d ? d.date.toISOString() : null });
        },
        [changes, setChanges],
    );

    return (
        <form onSubmit={handleSave} className="space-y-4 mb-4" data-testid="task-form">
            <Prompt
                when={isDirty}
                message="You have unsaved changes. Are you sure you want to discard them?"
            />
            <div>
                <span>Associated with</span>&nbsp;
                {entity ? (
                    props.entityLinkDisabled ? (
                        <span className="font-medium">{entity.displayName}</span>
                    ) : (
                        <Link className={defaultLinkStyle} to={`${entity.path}/tasks`}>
                            {entity.displayName}
                        </Link>
                    )
                ) : null}
            </div>
            <div className="space-y-4">
                <Input
                    label="Title"
                    autoFocus
                    value={changes.text ?? task.text ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setChanges({ ...changes, text: e.target.value })
                    }
                />
                <CalendarDateTimeInput
                    label="Due"
                    value={getCalendarDateTime(dueDate, dueDateTime)}
                    onChange={handleDueDateChange}
                />
                <UserSelect
                    includeEveryone={false}
                    includeUnassigned={mode === "update" && !storedOwnerId}
                    label="Assigned to"
                    value={changes.ownerId || defaultOwnerId}
                    onChange={handleOwnerChange}
                />
                <TextArea
                    label="Notes"
                    rows={3}
                    value={changes.notes ?? task.notes ?? ""}
                    onChange={e => setChanges({ ...changes, notes: e.target.value })}
                    autoGrow
                />
                <RecurrenceConfig
                    recurrencePattern={
                        changes.recurrencePattern ??
                        (task.recurrencePattern as RecurrencePattern)
                    }
                    recurrenceInterval={
                        changes.recurrenceInterval ?? task.recurrenceInterval
                    }
                    recurrenceDaysOfWeek={
                        (changes.recurrenceDaysOfWeek as DayOfWeek[]) ??
                        (task.recurrenceDaysOfWeek as DayOfWeek[])
                    }
                    recurrenceDayOfMonth={
                        changes.recurrenceDayOfMonth ?? task.recurrenceDayOfMonth
                    }
                    onChange={recurrenceChanges =>
                        setChanges({ ...changes, ...recurrenceChanges })
                    }
                />
                <ActivityTypeSelect
                    value={taskTypeId ?? null}
                    recordType={
                        entity ? activityRecordTypeForEntityKind(entity.kind) : null
                    }
                    onChange={handleTaskTypeChange}
                />
                {mode === "update" ? (
                    <>
                        <Checkbox
                            label="Completed"
                            checked={isCompleted}
                            onChange={e =>
                                setChanges({
                                    ...changes,
                                    status: e.target.checked ? "done" : "new",
                                    completedAt: e.target.checked
                                        ? new Date().toISOString()
                                        : null,
                                })
                            }
                        />
                        {isCompleted ? (
                            <CalendarDateTimeInput
                                label="Completed on"
                                value={completedAtValue}
                                onChange={handleCompletedAtChange}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>
            <div className="flex items-center space-x-2">
                <PrimaryButton
                    onClick={handleSave}
                    disabled={
                        updating ||
                        (mode === "create" && regionUsers.isLoading) ||
                        !task?.parentId
                    }>
                    <span className="text-sm">Save</span>
                </PrimaryButton>
                <SecondaryButton onClick={handleCancel}>
                    <span className="text-sm">Cancel</span>
                </SecondaryButton>
                {updating ? <Spinner /> : null}
                {props.onDelete && mode === "update" ? (
                    <span className="ml-auto">
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={props.onDelete}
                            confirm={{
                                title: "Delete Task",
                                message: "Are you sure you want to delete this task?",
                            }}
                        />
                    </span>
                ) : null}
            </div>
        </form>
    );
};
